import { useState, useRef, useEffect } from 'react';

const AirportDropdown = ({ 
  value, 
  onChange, 
  airports = [], 
  placeholder = 'Select Airport',
  loading = false,
  error = '',
  name = 'airport'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Find selected airport by ID or by IATA code (for backward compatibility)
  const selectedAirport = (airports || []).find(a => 
    a && (
      // eslint-disable-next-line eqeqeq
      a.id == value ||           // loose equality handles string "5" vs number 5
      (value && a.iata === value) // only match iata if value looks like a code, not a name
    )
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredAirports = (airports || []).filter(airport => {
    if (!airport) return false;
    const searchLower = (searchTerm || '').toLowerCase();
    return (
      (airport.airport_name || '').toLowerCase().includes(searchLower) ||
      (airport.country || '').toLowerCase().includes(searchLower) ||
      (airport.iata || '').toLowerCase().includes(searchLower)
    );
  });

  const handleSelect = (airport) => {
    // Pass the airport ID as the value (backend expects integer ID, not IATA code)
    onChange({ target: { name, value: airport.id } });
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name, value: '' } });
    setSearchTerm('');
  };

  const handleDropdownClick = () => {
    if (!loading) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm(''); // Clear search when opening
      }
    }
  };

  const inputClass = "w-full h-[34px] py-[6px] px-3 text-sm leading-[1.42857143] text-[#555] bg-white border border-[#ccc] rounded shadow-[inset_0_1px_1px_rgba(0,0,0,0.075)] transition-[border-color_box-shadow] duration-150 ease-in-out placeholder:text-[#999] focus:outline-none focus:border-[#66afe9] focus:shadow-[inset_0_1px_1px_rgba(0,0,0,0.075),0_0_8px_rgba(102,175,233,0.6)]";

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className={`${inputClass} cursor-pointer flex justify-between items-center pr-8`}
        onClick={handleDropdownClick}
      >
        <span className={selectedAirport ? 'text-[#555]' : 'text-[#999]'}>
          {loading ? 'Loading airports...' : selectedAirport ? `${selectedAirport.airport_name}, ${selectedAirport.country}` : placeholder}
        </span>
        {selectedAirport && (
          <span className="text-[#555] ml-2">{selectedAirport.iata}</span>
        )}
      </div>
      
      {/* Clear button */}
      {selectedAirport && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-8 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#555] bg-transparent border-none cursor-pointer p-1 transition-colors"
          title="Clear selection"
        >
          <i className="fas fa-times text-sm"></i>
        </button>
      )}
      
      {/* Dropdown arrow */}
      <div className={`absolute ${selectedAirport ? 'right-12' : 'right-3'} top-1/2 -translate-y-1/2 pointer-events-none transition-all`}>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-[#555] text-xs`}></i>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#ccc] rounded shadow-lg max-h-[300px] overflow-hidden">
          <div className="p-2 border-b border-[#eee]">
            <input
              type="text"
              className="w-full px-3 py-2 text-sm border border-[#ccc] rounded focus:outline-none focus:border-[#66afe9]"
              placeholder="Search airports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value || '')}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="overflow-y-auto max-h-[250px]">
            {filteredAirports.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[#999] text-center">
                No airports found
              </div>
            ) : (
              filteredAirports.map((airport) => (
                <div
                  key={airport.id}
                  className="px-3 py-2 cursor-pointer hover:bg-[#f5f5f5] flex justify-between items-center text-sm border-b border-[#f0f0f0] last:border-b-0"
                  onClick={() => handleSelect(airport)}
                >
                  <span className="text-[#555]">
                    {airport.airport_name}, {airport.country}
                  </span>
                  <span className="text-[#555] font-medium ml-2">
                    {airport.iata}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      
      {error && (
        <span className="text-[#a94442] block mt-[5px] text-sm">{error}</span>
      )}
    </div>
  );
};

export default AirportDropdown;
